<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title" v-text="pageTitle"></h1>
      </template>
      <template #actions>
        <page-header-action @click.prevent="backToDeployment">
          Back to Deployment
        </page-header-action>
        <page-header-action @click.prevent="toggleNotes">
          Notes
        </page-header-action>
      </template>
    </page-header>
    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <form-navigation-tabs
            :tabs="tabs"
            v-on:selected="setActive"
            :active="currentView"
          ></form-navigation-tabs>
          <div v-show="currentView === 'bodyshop'">
            <bodyshop :incident="incident"></bodyshop>
          </div>
          <div v-show="currentView === 'engineer'">
            <engineer :incident="incident"></engineer>
          </div>
          <div v-show="currentView === 'hire'">
            <div class="card-body">
              <div class="row">
                <div class="col-12 text-right">
                  <form-button
                    :indicator="indicator"
                    v-on:clicked="showAddHireForm"
                  >
                    Add Hire
                  </form-button>
                </div>
              </div>
            </div>
            <div class="card-body">
              <table class="table table-striped table-bordered table-centered">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Vehicle Registration</th>
                    <th>Hire Start Date</th>
                    <th>Total Daily Rate</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-bind:key="index" v-for="(hire, index) in hires">
                    <td>
                      <span
                        class="label label-lg"
                        :class="{
                          'label-default': hire.status.name === 'draft',
                          'label-success': hire.status.name === 'active',
                          'label-info': hire.status.name === 'created'
                        }"
                      >
                        {{ hire.status.label }}
                      </span>
                    </td>
                    <td>
                      {{
                        hire.vehicle && hire.vehicle.registration
                          ? hire.vehicle.registration
                          : 'Not Provided'
                      }}
                    </td>
                    <td>
                      {{
                        hire.vehicle && hire.vehicle.start_date
                          ? formatDate(hire.vehicle.start_date)
                          : 'Not Provided'
                      }}
                    </td>
                    <td>
                      {{
                        hire.vehicle &&
                        hire.vehicle.rates &&
                        hire.vehicle.rates.total_daily_rate !== null
                          ? '&pound;' +
                            penceToPounds(hire.vehicle.rates.total_daily_rate)
                          : 'Not Provided'
                      }}
                    </td>
                    <td>
                      <button
                        class="btn btn-primary"
                        v-on:click.prevent="viewHireForm(hire)"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <hire
              :incident="incident"
              :hire="hire"
              :modal-view="true"
              v-if="addHire"
              v-on:back="backToDeployment"
            ></hire>
            <div class="card-footer text-right" v-else>
              <button
                class="btn btn-primary btn-raised"
                name="save"
                v-on:click.prevent="submit"
              >
                <i class="icon icon-save"></i> Save
              </button>
            </div>
          </div>
          <vehicle-location
            :incident="incident"
            v-if="currentView == 'storage'"
            :show-recovery="false"
          ></vehicle-location>
          <div v-if="currentView === 'recovery'">
            <div class="card-body">
              <div class="col-lg-6">
                <toggleswitch
                  :labels="true"
                  name="self_recovery"
                  v-model="recovery.self_recovered"
                  helper="Are you/Did you recover the vehicle, or was it a contractor?"
                  label="Recovered by CHO?"
                ></toggleswitch>

                <selectlist
                  :options="recoveryCompanies"
                  label="Recovery Company"
                  label-name="name"
                  v-model="recovery.company_id"
                  placeholder="Please Select"
                  name="recovery_company"
                  v-show="!recovery.self_recovered"
                  helper="Please choose the company who recovered the vehicle"
                ></selectlist>
              </div>
              <div class="col-lg-6">
                <div
                  class="form-group has-helper"
                  :class="{ 'has-error': recovery.errors.has('recovered_at') }"
                >
                  <div class="col-md-4">
                    <label class="control-label">Recovered At:</label>
                    <p class="help-block">
                      Please enter the date the vehicle was recovered
                    </p>
                  </div>
                  <div class="col-md-8">
                    <datepicker
                      :value="recovery.recovered_at"
                      name="recovered_at"
                      v-on:selected="setRecoveredAt"
                      format="dd/MM/yyyy"
                      input-class="form-control"
                    ></datepicker>
                    <span
                      class="text-danger"
                      v-text="recovery.errors.get('recovered_at')"
                      v-if="recovery.errors.has('recovered_at')"
                    ></span>
                  </div>
                </div>
                <currencyinput
                  name="charge"
                  label="Recovery Charge"
                  v-model.number="recoveryCharge"
                  type="number"
                  helper="Please enter the recovery charge"
                ></currencyinput>
              </div>
            </div>
            <div class="card-footer text-right">
              <button
                class="btn btn-primary btn-raised"
                name="save"
                v-on:click.prevent="saveRecovery"
              >
                <i class="icon icon-save"></i> Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <notes-window
      :show="notes"
      :incident="incident"
      v-on:toggle-note="toggleNotes"
    ></notes-window>
  </div>
</template>

<script>
import Notes from './../Shared/Notes.vue';
import Hire from './Children/AddHire.vue';
import VehicleLocation from './../FirstNoticeOfLoss/Sections/VehicleLocation';
import BodyShop from './BodyShopEmail.vue';
import Engineer from './EngineerEmail.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },

    party: {
      type: String,
      required: true
    },

    client: {
      type: String,
      required: true
    }
  },

  components: {
    PageHeader,
    PageHeaderAction,
    'notes-window': Notes,
    hire: Hire,
    'vehicle-location': VehicleLocation,
    bodyshop: BodyShop,
    engineer: Engineer
  },

  computed: {
    pageTitle() {
      if (this.clientData.full_name) {
        return 'Deployment For Client: ' + this.clientData.full_name;
      }

      return 'Deployment';
    },

    recoveryCharge: {
      get() {
        return this.recovery.charge;
      },
      set: function(newValue) {
        if (newValue > 0) {
          this.recovery.charge = parseInt(this.poundsToPence(newValue));
          return;
        }

        this.recovery.charge = 0;
      }
    }
  },

  data() {
    return {
      indicator: false,
      currentView: 'hire',
      notes: false,
      clientData: {},
      addHire: false,
      hires: [],
      tabs: [
        {
          name: 'hire',
          label: 'Hire'
        },
        {
          name: 'storage',
          label: 'Storage'
        },
        {
          name: 'recovery',
          label: 'Recovery'
        },
        {
          name: 'bodyshop',
          label: 'Body shop'
        },
        {
          name: 'engineer',
          label: 'Engineer'
        }
      ],
      recoveryCompanies: [],
      recovery: new window.Form({
        self_recovered: false,
        company_id: null,
        recovered_at: null,
        charge: 0
      })
    };
  },

  mounted() {
    window.Ajax.get(`/incidents/${this.incident}`)
      .then(response => {
        if (response.data.plus) {
          this.addFnolPlusTabs();
        }

        this.$store.dispatch('Incident/setIncident', response.data);

        this.clientData = this.$store.getters['Incident/client'];
        let recovery = this.$store.getters['Incident/recovery'];

        if (recovery) {
          this.recovery.self_recovered = recovery.self_recovered;
          this.recovery.company_id = recovery.company_id;
          this.recovery.recovered_at = recovery.recovered_at;
          this.recovery.charge = recovery.charge;
        }

        window.Ajax.get('/companies?type=recovery')
          .then(response => {
            this.recoveryCompanies = response.data;
          })
          .catch(response => {});

        window.Ajax.get(
          `/incidents/${this.incident}/parties/${this.party}/hires`
        )
          .then(response => {
            this.hires = response.data;
          })
          .catch(error => {});
      })
      .catch(error => {});
  },

  methods: {
    addFnolPlusTabs() {
      this.tabs.push([
        {
          name: 'personal_injury',
          label: 'Personal Injury'
        },
        {
          name: 'salvage',
          label: 'Salvage'
        }
      ]);
    },

    viewHireForm(hire) {
      if (!hire.has_vehicle_hire_agreement) {
        this.route(
          `/deployment/${this.incident}/party/${this.party}/hire/${hire.identifier}`
        );

        return;
      }

      this.route(
        `/deployment/${this.incident}/party/${this.party}/hire/${hire.identifier}/complete`
      );
    },

    showAddHireForm() {
      this.indicator = true;

      window.Ajax.post(
        `/incidents/${this.incident}/parties/${this.party}/hires`
      )
        .then(response => {
          this.indicator = false;
          this.route(
            `/deployment/${this.incident}/party/${this.party}/hire/${response.data.identifier}`
          );
        })
        .catch(error => {
          this.indicator = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an issue creating the hire'
          );
        });
    },

    closeModal() {
      this.addHire = false;
    },

    setActive(tab) {
      this.currentView = tab;
    },

    backToDeployment() {
      this.route(`/deployment/${this.incident}`);
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    setRecoveredAt(value) {
      this.form.recovered_at = value;
    },

    saveRecovery() {
      this.indicator = true;
      let url = `/incidents/${this.incident}/recovery`;
      let method = 'post';

      if (this.$store.getters['Incident/recovery']) {
        url = `${url}/${this.$store.getters['Incident/recovery'].identifier}`;
        method = 'put';
      }

      this.recovery[method](url)
        .then(response => {
          this.indicator = true;
          window.toastify.success(
            'Success!',
            'The recovery details were saved successfully.'
          );
        })
        .catch(error => {
          this.indicator = false;
          window.toastify.error(
            'Error!',
            'Sorry, there was an error saving the details'
          );
        });
    }
  },

  beforeDestroy() {
    this.$store.commit('Incident/clearRecord');
  }
};
</script>
